import { FloorsFilter } from 'components/eventView/filters/FloorsFilter';
import { MapWrapper } from 'components/eventView/pages/MapWrappers/MapWrapper';
import { ReverseButton } from 'elements/eventView/atomic/ReverseButton';
import { useAnalytics } from 'libs/utils/site/analytics';
import { useContext, useEffect, useMemo, useState } from 'react';
import { useLocation, useNavigate } from 'react-router';
import { useSearchParams } from 'react-router-dom';
import { CtxEventView } from 'session/ctxEventView';
import { Localization } from 'static/localization/localization';
import styled from 'styled-components/macro';

interface Props {
    className?: string;
}

export const EventViewMapPageContent = (props: Props) => {
    const ctxEventView = useContext(CtxEventView);
    const { eventData, eventPath, visibleFloorIds, isWide, mapEntityState } = ctxEventView;

    const [renderHash, setRenderHash] = useState(Math.random().toString());

    const [searchParams, setSearchParams] = useSearchParams();

    const navigate = useNavigate();
    const location = useLocation();
    const analytics = useAnalytics();

    const markerId: string | null = searchParams.get('markerId');
    const zoomTo: string | null = searchParams.get('zoomTo');
    const isReset: string | null = searchParams.get('reset');

    useEffect(() => {
        if (location.pathname.includes('/map')) {
            setRenderHash(Math.random().toString());
        }
    }, [location.pathname]);

    const getNewMapPosition = () => {
        if (markerId) {
            const shouldZoomTo = zoomTo === '1';
            const result = {
                type: 'marker' as const,
                markerId,
                zoomTo: !ctxEventView.isWide || shouldZoomTo
            };

            if (shouldZoomTo) {
                searchParams.delete('zoomTo');
                setSearchParams(searchParams, { replace: true });
            }

            return result;
        }
        if (isReset) {
            searchParams.delete('reset');
            setSearchParams(searchParams, { replace: true });
            return { type: 'reset' as const };
        }

        return null;
    };

    const newVisibleMapSetIds = [
        mapEntityState?.filterIds ?? ctxEventView.categories.map((e) => e.categoryId) ?? [],
        visibleFloorIds ?? []
    ].flat();

    const newMapPosition = useMemo(() => {
        return getNewMapPosition();
    }, [markerId, isReset, zoomTo]);

    function handleMarkerSelection({ storyId, markerId }) {
        analytics.track('event_ui.detail.open_clicked', {
            type: 'map',
            storyId,
            title:
                ctxEventView.allStories?.find((e) => e.storyId === storyId)?.title ??
                ctxEventView.allPrograms?.find((e) => e.storyId === storyId)?.title
        });

        if (isWide) {
            searchParams.set('detailId', storyId);
            searchParams.set('markerId', markerId);
            setSearchParams(searchParams);
        } else {
            searchParams.delete('markerId');
            setSearchParams(searchParams, { replace: true });
            navigate(location.pathname + '/' + storyId);
        }
    }

    const mapEntities = eventData.eventInfo.otherEntities?.filter((e) => e.isMapEntity);
    return (
        <S_EVMapPageContent className={'event-experience-mapbox ' + props.className}>
            <MapWrapper
                preloadedData={{
                    worldMap: ctxEventView.rawGraphQLData.map,
                    stories: ctxEventView.rawGraphQLData.stories
                }}
                onMarkerSelectedOverride={handleMarkerSelection}
                mapId={eventData.mapId}
                hideFloors
                mapProps={{
                    light: {
                        anchor: 'viewport',
                        color: 'white',
                        intensity: 0.15
                    }
                }}
                newMapPosition={newMapPosition}
                newVisiblMapSetIds={newVisibleMapSetIds}
                forceResizeHash={renderHash}
                topOverflowPosition={'right'}
                topOverflow={
                    <FloorsFilter
                        newMarkerId={
                            newMapPosition?.type === 'marker' ? newMapPosition.markerId : null
                        }
                    />
                }
                markersCustomConfig={{
                    standScale: eventData.mapSettings?.standZoom ?? 1
                }}
            />
            {!isWide && mapEntities.length === 1 && (
                <S_ReverseButton
                    iconName="clipboard-list"
                    text={Localization.LIST_VIEW}
                    onClick={() => {
                        navigate(`/event/${eventPath}/${mapEntities[0].id}/list`);
                        analytics.track('event_ui.navigate.clicked', {
                            source: 'switch',
                            destination: 'entities'
                        });
                    }}
                />
            )}
        </S_EVMapPageContent>
    );
};

const S_EVMapPageContent = styled.div`
    display: flex;
    flex-direction: column;
`;

const S_ReverseButton = styled(ReverseButton)`
    position: absolute;
    left: 10px;
    top: 15px;
    z-index: 2;
    min-width: 130px;
`;
